/* RunScreenComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./RunScreenRenderer");
const ScheduleVeilComponent = require("../../../../lib/actions/veils/schedule/ScheduleVeilComponent");
const Registrations = require("../../../../../RegistrationNamespaces");
const _ = require("lodash");

class RunScreenComponent extends EventEmitter {
    constructor(
        logger,
        GameEditor,
        OsUpgradePlan,
        DeviceEntity,
        ActiveView,
        modalUserFeedback,
        userSession
    ) {
        super();
        this._logger = logger;
        this._gameEditor = GameEditor;
        this._osPlanEntity = OsUpgradePlan;
        this._activeView = ActiveView;
        this._modalUserFeedback = modalUserFeedback;
        this._planRunTargetCount = undefined;

        this._userSession = userSession;
        this._renderer = new Renderer(this);
        this._registerListeners();
        this._renderer.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    get logger() {
        return this._logger;
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._logger = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "RunScreenComponent.shutdown() error.");
            }
        );
    }

    // prettier-ignore
    set "$teve.ui.context" (context) { //jshint ignore:line
        this._context = context;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    schedulePlan(workInput) {
        let selectedID = this._activeView.getSelectedPlanIds().selectedRows;
        let titleMsg = "Run OS Plan - Schedule";
        let entityValues = {};
        return this._activeView.pagingFeed
            .getOsPlanEntitiesById(selectedID)
            .then((entities) => {
                entityValues = entities;
                return this._checkPlanValidated(entities);
            })
            .then(() => {
                return this._modalUserFeedback.displayCustom(
                    new ScheduleVeilComponent(this._logger, false, titleMsg, {
                        workType: "plan",
                        firstTime: true,
                    }),
                    {
                        dontHide: false,
                    }
                );
            })
            .then((scheduleDef) => {
                let schedule = scheduleDef.schedule;
                let description = workInput.description;
                let workInputObj = scheduleDef.workInput;
                workInputObj.description = description;
                // backdoor to allow every minute schedules for internal testing
                if (_.get(schedule, "every.hour") && name === "everymin") {
                    _.set(schedule, "every.minute", schedule.every.hour);
                    delete schedule.every.hour;
                }
                if (entityValues && entityValues.length === 1) {
                    return entityValues[0].createPlanSchedule(
                        description,
                        "",
                        schedule,
                        workInputObj
                    );
                } else if (entityValues && entityValues.length > 1) {
                    return this._osPlanEntity.createPlansSchedule(
                        this._userSession.activeOrganization.id,
                        entityValues.map((entity) => {
                            return entity.id;
                        }),
                        description,
                        `Plans: ${entityValues
                            .map((entity) => {
                                return `"${entity.name}"`;
                            })
                            .join(", ")}`,
                        schedule,
                        workInputObj
                    );
                }
            })
            .then(() => this._modalUserFeedback.hideDisplayed())
            .catch((err) => {
                if (err.notValidated) {
                    const errMessage = `All plans require validation before they can be run.`;
                    this._modalUserFeedback.displayError({
                        error: {
                            errorMsg: errMessage,
                        },
                    });
                } else if (!err.userRejected) {
                    if (this._logger) {
                        this._logger.warn(
                            {error: err},
                            "RunScreenComponent._startRunWork() - Error"
                        );
                    } else {
                        console.error(err);
                    }

                    this._modalUserFeedback.displayError(err);
                }
            });
    }

    startPlan(/*, workInput*/) {
        let workTarget = this._getWorkTarget();
        let selectedID = this._activeView.getSelectedPlanIds().selectedRows;
        /**
         *
         * @param {Object} progress - progress data object
         * @param {number} progress.startTime
         * @param {number} progress.targetCount
         * @param {number} progress.successCount
         * @param {number} progress.failureCount
         * @param {string} progress.planId
         * @param {string} progress.planName
         * @param {string} progress.planDescription
         */
        const progressCB = ({successCount, failureCount, targetCount}) => {
            this._modalUserFeedback.showLoadingProgress(
                `Succeeded ${successCount}, failed ${failureCount} of ${targetCount} plan${
                    targetCount > 1 ? "s" : ""
                }`
            );
        };

        return this._activeView.pagingFeed
            .getOsPlanEntitiesById(selectedID)
            .then((entities) => {
                return this._checkPlanValidated(entities);
            })
            .then(() => {
                if (this._modalUserFeedback) {
                    this._modalUserFeedback.displayLoading({
                        message: "Running your plan... This could take a moment",
                    });
                }
            })
            .then(() => {
                return this._osPlanEntity.runPlansWork(
                    this._userSession.activeOrganization.id,
                    workTarget,
                    undefined,
                    progressCB
                );
            })
            .then((result) => {
                let userNotificationMessage = "";
                if (!result.summary) {
                    userNotificationMessage = `Total number of devices: ${
                        result._trimmed.targetResults || 0
                    }`;
                } else {
                    const totalPlans = result.summary.targetCount;
                    this._planRunTargetCount = totalPlans;
                    const plansSuccess = result.summary.successCount;
                    const plansFail = result.summary.failureCount;
                    const missingPlans = result.summary.missingTargetCount;

                    const devicesTotal = _.isUndefined(result.summary.deviceTargetCount)
                        ? null
                        : result.summary.deviceTargetCount;

                    const devicesSuccess = _.isUndefined(result.summary.deviceSuccessCount)
                        ? null
                        : result.summary.deviceSuccessCount;

                    const devicesFailure = _.isUndefined(result.summary.deviceFailureCount)
                        ? null
                        : result.summary.deviceFailureCount;

                    if (totalPlans > 1) {
                        userNotificationMessage = [
                            `Total plans: ${totalPlans || 0}`,
                            `Completed plans: ${plansSuccess || 0}`,
                            `Error plans: ${plansFail || 0}`,
                            `Missing plans: ${missingPlans || 0}`,
                        ];

                        if (
                            !(
                                _.isNull(devicesTotal) &&
                                _.isNull(devicesSuccess) &&
                                _.isNull(devicesFailure)
                            )
                        ) {
                            userNotificationMessage = _.concat(userNotificationMessage, [
                                "",
                                "",
                                `Total devices: ${devicesTotal || 0}`,
                                `Successful devices: ${devicesSuccess || 0}`,
                                `Failed devices: ${devicesFailure || 0}`,
                            ]);
                        }
                        userNotificationMessage = userNotificationMessage.join("\r\n");
                    } else {
                        if (
                            !(
                                _.isNull(devicesTotal) &&
                                _.isNull(devicesSuccess) &&
                                _.isNull(devicesFailure)
                            )
                        ) {
                            userNotificationMessage = [
                                `Total devices: ${devicesTotal || 0}`,
                                `Successful devices: ${devicesSuccess || 0}`,
                                `Failed devices: ${devicesFailure || 0}`,
                            ].join("\r\n");
                        } else {
                            userNotificationMessage = [
                                `Plan Execution ${plansSuccess ? "Successful" : "Failed"}`,
                            ];
                        }
                    }
                }

                return this._getVeilType(userNotificationMessage).then(() => {
                    if (result.targets && !_.isEmpty(result.targets)) {
                        let firstPlanID = Object.keys(result.targets)[0]; // Open First plan after when click on view result
                        let planDetails = result.targets[firstPlanID];
                        return this._gameEditor.launchPlanEditor(
                            {
                                fqn: Registrations.OS_PLAN_EDITOR_VIEW_RESULTS,
                            },
                            {
                                id: planDetails.id,
                                title: _.get(planDetails, "name", ""),
                            }
                        );
                    } else {
                        this._logger.warn(
                            {error: {}},
                            "RunScreenComponent._startRunWork() - No policyId received"
                        );
                        return Promise.reject();
                    }
                });
            })
            .catch((err) => {
                if (err !== undefined) {
                    if (err.notValidated) {
                        const errMessage = `All plans require validation before they can be run.`;
                        this._modalUserFeedback.displayError({
                            error: {
                                errorMsg: errMessage,
                            },
                        });
                    } else if (!err.userRejected) {
                        if (this._logger) {
                            this._logger.warn(
                                {error: err},
                                "RunScreenComponent._startRunWork() - Error"
                            );
                        } else {
                            console.error(err);
                        }

                        this._modalUserFeedback.displayError(err);
                    }
                }
            });
    }

    _getVeilType(userNotificationMessage) {
        if (this._planRunTargetCount === 1) {
            return this._modalUserFeedback.displayConfirmation(userNotificationMessage, {
                title: "Run Completed",
                confirmDetails: {
                    text: "View Results",
                    type: "additive",
                },
                rejectDetails: {
                    text: "OK",
                    type: "text",
                },
            });
        } else {
            return this._modalUserFeedback
                .displayNotification(userNotificationMessage, {
                    title: "Run Completed",
                })
                .then(() => {
                    return Promise.reject();
                });
        }
    }

    cancelRun() {
        this._context.reject({userRejected: true});
    }
    /**
     * Create workTarget Object from devices component selection
     * @param {String} selection
     * @returns {Object}
     */
    _getWorkTarget() {
        let workTarget = {
            category: "entity",
            entityType: "OsUpgradePlan",
            method: "multi",
            ids: this._activeView.getSelectedPlanIds().selectedRows,
        };
        return workTarget;
    }

    _checkPlanValidated(entities) {
        if (_.isEmpty(entities.filter((item) => item.getStatus() !== "VALIDATED"))) {
            return Promise.resolve();
        } else {
            return Promise.reject({
                notValidated: true,
            });
        }
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = RunScreenComponent;
